/**
 * Contexte d'authentification : gère l'utilisateur courant et le jeton JWT.
 * États : undefined (vérification en cours), null (non connecté), objet user (connecté).
 * Multi-établissements : purge l'état local au changement de propriétaire (user/établissement)
 * et vide le cache React Query au login/logout.
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authApi, TOKEN_KEY, getToken, clearTokens } from '../services/api';

const AuthContext = createContext(null);
const OWNER_KEY = 'vd_owner';

/** Vide l'état local lié à un propriétaire (autosave, brouillons, préférences, hors-ligne). */
function purgeLocalState() {
  try {
    const toRemove = ['vd-noter-draft', 'vd_public_mode', 'vd-stock-view'];
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith('vd-autosave::')) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* noop */ }
  try { indexedDB.deleteDatabase('vd-offline'); } catch (e) { /* noop */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const queryClient = useQueryClient();

  useEffect(() => {
    const token = getToken();
    if (!token) { setUser(null); return; }
    authApi.me().then(setUser).catch(() => {
      clearTokens();
      setUser(null);
    });
    const onForcedLogout = () => {
      clearTokens();
      setUser(null);
    };
    window.addEventListener('vd-logout', onForcedLogout);
    return () => window.removeEventListener('vd-logout', onForcedLogout);
  }, []);

  const login = useCallback(async (email, password) => {
    const { token, user: u } = await authApi.login(email, password);
    const owner = { user_id: u.id, tenant_id: u.tenant?.id || null };
    let prev = null;
    try { prev = JSON.parse(localStorage.getItem(OWNER_KEY) || 'null'); } catch (e) { /* noop */ }
    localStorage.setItem(TOKEN_KEY, token);
    if (!prev || prev.user_id !== owner.user_id || prev.tenant_id !== owner.tenant_id) {
      purgeLocalState();
    }
    try {
      localStorage.setItem(OWNER_KEY, JSON.stringify(owner));
      localStorage.setItem('vd_last_account', JSON.stringify({ name: u.name, email: u.email }));
    } catch (e) { /* noop */ }
    queryClient.clear();
    setUser(u);
    return u;
  }, [queryClient]);

  const logout = useCallback(() => {
    clearTokens();
    localStorage.removeItem('vd_last_account');
    purgeLocalState();
    queryClient.clear();
    setUser(null);
  }, [queryClient]);

  const refreshUser = useCallback(async () => {
    const u = await authApi.me();
    setUser(u);
    return u;
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshUser, isSuperadmin: !!user?.is_superadmin, tenant: user?.tenant || null }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/** Droits financiers : gérant et sommelier voient achats/marges. */
export function canSeeFinancials(user) {
  return user && (user.role === 'gerant' || user.role === 'sommelier');
}
